import { EditFilled, WarningOutlined } from '@ant-design/icons';
import { Badge, Button, Col, Popover, Row, Space, Table } from 'antd';
import { ColumnsType, TableProps } from 'antd/es/table';
import { EmployeeActionsPopover } from 'components/employees/EmployeeActionsPopover';
import { useMemo } from 'react';
import { EmployeeActions } from 'types/employeeActions';
import User from 'types/user';

const tableProps: TableProps<User> = {
  bordered: true,
  tableLayout: 'fixed',
  size: 'small',
  className: 'table-ps',
  pagination: { position: ['bottomCenter'], pageSize: 50 },
};

interface UserTableProps {
  actions: EmployeeActions;
  activeContractUserIds: ReadonlySet<string>;
  impersonatingEmployeeId: string | null;
  loading: boolean;
  users: User[];
}

export const UserTable = ({
  actions,
  activeContractUserIds,
  impersonatingEmployeeId,
  loading,
  users,
}: UserTableProps) => {
  const columns: ColumnsType<User> = useMemo(
    () => [
      {
        title: 'ID',
        align: 'center',
        defaultSortOrder: 'ascend',
        sorter: (a, b) => Number(a.idEmployee) - Number(b.idEmployee),
        responsive: ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'],
        width: 150,
        render: (record: User) => (
          <Row align="middle" justify="center">
            <Col>
              <p>{record.idEmployee}</p>
            </Col>
            {activeContractUserIds.has(record._id) && (
              <Col className="ml-2">
                <Popover content="Este empleado tiene al menos un contrato activo sin horarios asignados.">
                  <WarningOutlined className="list-warning-icon" />
                </Popover>
              </Col>
            )}
          </Row>
        ),
      },
      {
        title: 'Nombre completo',
        align: 'center',
        responsive: ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'],
        sorter: (a, b) =>
          `${a.name} ${a.lastName}`.localeCompare(`${b.name} ${b.lastName}`),
        render: (record: User) => (
          <Button
            type="link"
            data-cy={`employee-name-${record.idEmployee}`}
            onClick={() => actions.onOpenDetail(record)}
          >
            {record.name} {record.lastName}
          </Button>
        ),
      },
      {
        title: 'Correo electrónico',
        dataIndex: 'email',
        align: 'center',
        responsive: ['lg', 'xl', 'xxl'],
        sorter: (a, b) => a.email.localeCompare(b.email),
      },
      {
        title: 'Estatus',
        align: 'center',
        responsive: ['lg', 'xl', 'xxl'],
        width: 200,
        render: (record: User) => (
          <div>
            {record.status ? (
              <Space data-cy={`status-active-${record._id}`}>
                <Badge status="success" />
                Activo
              </Space>
            ) : (
              <Space data-cy={`status-inactive-${record._id}`}>
                <Badge status="error" />
                Inactivo
              </Space>
            )}
            <Popover
              content={
                record.status ? (
                  <Button
                    data-cy={`button-inactive-${record._id}`}
                    type="primary"
                    danger
                    block
                    onClick={() => void actions.onChangeStatus(record)}
                  >
                    Desactivar
                  </Button>
                ) : (
                  <Button
                    data-cy={`button-active-${record._id}`}
                    type="primary"
                    block
                    onClick={() => void actions.onChangeStatus(record)}
                  >
                    Activar
                  </Button>
                )
              }
              trigger="click"
              placement="left"
            >
              <EditFilled
                data-cy={`edit-status-${record._id}`}
                style={{ marginLeft: 5 }}
              />
            </Popover>
          </div>
        ),
      },
      {
        title: 'Opciones',
        align: 'center',
        responsive: ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'],
        width: 100,
        render: (record: User) => (
          <EmployeeActionsPopover
            actions={actions}
            employee={record}
            impersonatingEmployeeId={impersonatingEmployeeId}
          />
        ),
      },
    ],
    [actions, activeContractUserIds, impersonatingEmployeeId]
  );

  return (
    <Table
      data-cy="employeesTable"
      {...tableProps}
      loading={loading}
      dataSource={users}
      columns={columns}
      id="employeesTable"
      rowKey="idEmployee"
    />
  );
};
